<script setup lang="ts">
import AppPublic from '@/layouts/AppPublic.vue';
import TryItSection from '@/components/landing/TryItSection.vue';
import { usePageHead, productJsonLd } from '@/composables/usePageHead';

// inbox-110 / roadmap G7 — полноценный лендинг.
// Port структуры из docs/mockups/landing-v1.html без pill 152-ФЗ.
// inbox-131 — TryItSection (anon-link create) встроен между Hero и Features.
// Секции: hero → try-it → features (6) → AI → integrations → pricing → final CTA.

usePageHead({
  title: 'link-platform',
  description: 'Российская платформа коротких ссылок, QR-кодов и аналитики. Свои домены, A/B-тесты, антифрод, AI-копайлот на YandexGPT.',
  path: '/',
  type: 'website',
  jsonLd: productJsonLd(
    'link-platform',
    'Российская платформа коротких ссылок, QR-кодов и аналитики для маркетинга и продаж.',
  ),
});

const features = [
  {
    icon: 'route',
    title: 'Smart Routing',
    text: 'Один URL, разные target по гео, устройству, времени, языку. UTM-инжект и shadowban-роутинг для антифрода.',
  },
  {
    icon: 'chart',
    title: 'Глубокая аналитика',
    text: 'Реалтайм-клики с детализацией: гео-карта, devices, source, UTM-разрез. ETL в ClickHouse при росте до 10M+ событий.',
  },
  {
    icon: 'shield',
    title: 'Антифрод ML',
    text: 'Классификация фрод-сегментов: бот-фермы, прокси-пулы, аномальные паттерны. Блокировка по политике или ручное ревью.',
  },
  {
    icon: 'qr',
    title: 'QR-коды и Deep Links',
    text: 'Динамические QR с лого и фирменными цветами. iOS/Android Universal Links — открытие сразу в приложении.',
  },
  {
    icon: 'globe',
    title: 'Кастомные домены',
    text: 'go.your-brand.ru вместо короткого хоста. Авто-выпуск SSL, верификация через TXT, edge-routing.',
  },
  {
    icon: 'bio',
    title: 'Bio-страницы',
    text: 'Лендинг-визитки на коротком адресе. Drag-drop виджеты, формы, продукты, интеграция с CRM.',
  },
];

const integrations = [
  'Яндекс.Метрика', 'Google Analytics 4', 'Telegram Bot API', 'VK Реклама',
  'AppsFlyer', 'MyTracker', 'Slack', 'Zapier', 'amoCRM', 'Bitrix24', 'Mailchimp', 'Sendsay',
];
</script>

<template>
  <AppPublic>
    <!-- HERO -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">AI-копайлот на YandexGPT · Beta</div>
          <h1 class="hero-title text-[32px] sm:text-[42px] lg:text-[56px]">
            Короткие ссылки <span class="grad">с аналитикой,</span><br />
            QR-кодами и AI
          </h1>
          <p class="hero-sub">
            Платформа для маркетологов, продактов и инфра-команд. Сокращайте URL, отслеживайте
            клики в реальном времени, защищайтесь от ботов. Свои домены, REST API, ru-локализация.
          </p>
          <div class="hero-cta flex flex-col sm:flex-row sm:flex-wrap sm:justify-center gap-3">
            <a class="btn-primary w-full sm:w-auto" href="/signup">Начать бесплатно</a>
            <a class="btn-secondary w-full sm:w-auto" href="#features">Возможности →</a>
          </div>
          <div class="hero-trust">
            <span class="trust-pill">До 50 ссылок бесплатно</span>
            <span class="trust-pill">Без карты при регистрации</span>
            <span class="trust-pill">Старт от 990 ₽/мес</span>
          </div>
        </div>

        <div class="demo-wrap" aria-hidden="true">
          <div class="demo">
            <div class="demo-bar">
              <div class="dot"></div><div class="dot"></div><div class="dot"></div>
              <div class="url">app.link-platform.ru/dashboard</div>
            </div>
            <div class="demo-body">
              <div class="demo-sidebar">
                <div class="demo-side-item active">Обзор</div>
                <div class="demo-side-item">Ссылки</div>
                <div class="demo-side-item">Кампании</div>
                <div class="demo-side-item">Аналитика</div>
                <div class="demo-side-item">Антифрод</div>
                <div class="demo-side-item">AI-копайлот</div>
                <div class="demo-side-item">Интеграции</div>
              </div>
              <div class="demo-main">
                <div class="demo-h">
                  <h3>Обзор кампаний</h3>
                  <span class="period">Последние 30 дней</span>
                </div>
                <div class="demo-stats">
                  <div class="stat"><div class="label">Клики</div><div class="value">847 392</div><div class="delta up">+23.4%</div></div>
                  <div class="stat"><div class="label">Уникальные</div><div class="value">312 580</div><div class="delta up">+18.1%</div></div>
                  <div class="stat"><div class="label">CR</div><div class="value">4.82%</div><div class="delta up">+0.7 пп</div></div>
                  <div class="stat"><div class="label">Фрод</div><div class="value">2.1%</div><div class="delta down">−1.4 пп</div></div>
                </div>
                <div class="demo-chart">
                  <div class="bar" style="height: 32%"></div>
                  <div class="bar" style="height: 45%"></div>
                  <div class="bar" style="height: 38%"></div>
                  <div class="bar" style="height: 62%"></div>
                  <div class="bar" style="height: 51%"></div>
                  <div class="bar" style="height: 74%"></div>
                  <div class="bar" style="height: 68%"></div>
                  <div class="bar" style="height: 82%"></div>
                  <div class="bar" style="height: 71%"></div>
                  <div class="bar" style="height: 89%"></div>
                  <div class="bar" style="height: 78%"></div>
                  <div class="bar" style="height: 95%"></div>
                  <div class="bar" style="height: 84%"></div>
                  <div class="bar" style="height: 91%"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- TRY IT NOW (inbox-131) -->
    <TryItSection />

    <!-- FEATURES -->
    <section class="features" id="features">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Возможности</span>
          <h2>Не просто сокращалка</h2>
          <p class="section-sub">
            Полноценная инфра для отслеживания трафика, генерации QR-кодов и работы с
            ссылками на масштабе. Шесть встроенных модулей — без дополнительных подписок.
          </p>
        </div>
        <div class="feature-grid">
          <article v-for="f in features" :key="f.title" class="feature">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
                <g v-if="f.icon === 'route'"><circle cx="6" cy="19" r="3"/><circle cx="18" cy="5" r="3"/><path d="M9 19h6a4 4 0 0 0 4-4V9"/></g>
                <g v-else-if="f.icon === 'chart'"><path d="M3 3v18h18"/><path d="m7 14 4-4 4 4 5-5"/></g>
                <g v-else-if="f.icon === 'shield'"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></g>
                <g v-else-if="f.icon === 'qr'"><rect x="4" y="4" width="6" height="6"/><rect x="14" y="4" width="6" height="6"/><rect x="4" y="14" width="6" height="6"/><path d="M14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z"/></g>
                <g v-else-if="f.icon === 'globe'"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a14.5 14.5 0 0 1 0 20"/></g>
                <g v-else><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></g>
              </svg>
            </div>
            <h3>{{ f.title }}</h3>
            <p>{{ f.text }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- AI -->
    <section class="ai-section">
      <div class="container">
        <div class="ai-grid">
          <div class="ai-content">
            <span class="section-eyebrow">AI-копайлот</span>
            <h2>Маркетолог спрашивает —<br />AI отвечает</h2>
            <p class="ai-text">
              «Какие 5 ссылок принесли больше всего конверсий за квартал?», «Аномалии в кликах
              за последние сутки?», «Подбери UTM-метку для рассылки Q3». YandexGPT работает
              поверх вашей собственной аналитики, а не общественной базы.
            </p>
            <ul class="ai-features">
              <li>Отвечает на 80% повседневных вопросов без открытия дашборда</li>
              <li>Генерирует UTM-наборы и предлагает варианты A/B-тестов</li>
              <li>Замечает аномалии трафика раньше, чем вы откроете отчёт</li>
            </ul>
            <a class="btn-secondary" href="/ai">Подробнее об AI →</a>
          </div>
          <div class="ai-chat" aria-hidden="true">
            <div class="msg user">Топ-5 ссылок по конверсиям за квартал?</div>
            <div class="msg ai">
              <strong>5 ссылок с наибольшим CR за Q2:</strong>
              <ol>
                <li><code>link.helmix.app/affiliate-k</code> — CR 9,1%</li>
                <li><code>link.helmix.app/spring26</code> — CR 6,8%</li>
                <li><code>link.helmix.app/email-may-a</code> — CR 5,6%</li>
                <li><code>link.helmix.app/q2promo</code> — CR 5,2%</li>
                <li><code>link.helmix.app/menu</code> — CR 3,1%</li>
              </ol>
              <span class="msg-meta">YandexGPT · 2 сек</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- INTEGRATIONS -->
    <section class="integrations">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Интеграции</span>
          <h2>Подключается к вашей экосистеме</h2>
          <p class="section-sub">Аналитика, реклама, CRM, рассылки, мессенджеры — 12+ готовых интеграций. Custom через REST API и webhooks.</p>
        </div>
        <div class="int-grid">
          <span v-for="i in integrations" :key="i" class="int-chip">{{ i }}</span>
        </div>
      </div>
    </section>

    <!-- PRICING TEASER -->
    <section id="pricing" class="pricing">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Тарифы</span>
          <h2>Простые и понятные тарифы</h2>
          <p class="section-sub">Free навсегда. Старт от 990 ₽/мес. Без скрытых платежей. Закрывающие документы для юр.лиц.</p>
        </div>
        <div class="pricing-grid">
          <article class="price-card">
            <div class="tier-name">Free</div>
            <div class="price">0 ₽<small>/мес</small></div>
            <div class="price-desc">Чтобы попробовать без обязательств</div>
            <ul>
              <li>50 ссылок в месяц</li>
              <li>Базовая аналитика</li>
              <li>QR-коды статика</li>
              <li>Сообщество</li>
            </ul>
            <a href="/auth/signup" class="btn-outline">Начать</a>
          </article>
          <article class="price-card">
            <div class="tier-name">Старт</div>
            <div class="price">990 ₽<small>/мес</small></div>
            <div class="price-desc">Для ИП и соло-маркетологов</div>
            <ul>
              <li>500 ссылок в месяц</li>
              <li>Smart Time (AI)</li>
              <li>QR с лого</li>
              <li>Bio-страница</li>
            </ul>
            <a href="/auth/signup" class="btn-outline">Выбрать</a>
          </article>
          <article class="price-card featured">
            <div class="featured-tag">Популярный</div>
            <div class="tier-name">Бизнес</div>
            <div class="price">4 990 ₽<small>/мес</small></div>
            <div class="price-desc">Для СМБ и performance-команд</div>
            <ul>
              <li>5 000 ссылок в месяц</li>
              <li>AI-копайлот безлимит</li>
              <li>A/B-тесты + Smart Routing</li>
              <li>1 кастомный домен</li>
              <li>REST API</li>
              <li>Антифрод ML</li>
            </ul>
            <a href="/auth/signup" class="btn-primary">Выбрать</a>
          </article>
          <article class="price-card">
            <div class="tier-name">Команда</div>
            <div class="price">14 990 ₽<small>/мес</small></div>
            <div class="price-desc">Для агентств и крупных брендов</div>
            <ul>
              <li>50 000 ссылок в месяц</li>
              <li>Мульти-юзер + роли</li>
              <li>White-label</li>
              <li>5 кастомных доменов</li>
              <li>SSO (Yandex 360 / VK ID)</li>
            </ul>
            <a href="/auth/signup" class="btn-outline">Выбрать</a>
          </article>
        </div>
        <p class="pricing-foot">Нужно больше? <a href="/about#enterprise">Enterprise-тариф</a> — on-premise, SLA 99.99%, кастомные интеграции.</p>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="final-cta">
      <div class="container">
        <h2>Готовы попробовать?</h2>
        <p>Регистрация за 30 секунд. Без карты. 50 ссылок навсегда.</p>
        <div class="final-cta-actions flex flex-col sm:flex-row sm:flex-wrap sm:justify-center gap-3">
          <a class="btn-primary big w-full sm:w-auto" href="/auth/signup">Начать бесплатно</a>
          <a class="btn-secondary w-full sm:w-auto" href="/auth/login">Войти</a>
        </div>
      </div>
    </section>
  </AppPublic>
</template>

<style scoped>
.container { max-width: 1180px; margin: 0 auto; padding: 0 24px; }

/* Hero */
.hero {
  padding: 80px 0 100px;
  background:
    radial-gradient(ellipse 80% 60% at 50% 0%, rgba(79, 140, 255, 0.18), transparent 60%),
    radial-gradient(ellipse 60% 50% at 80% 30%, rgba(179, 102, 255, 0.10), transparent 65%);
}
.hero-content {
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
}
.hero-content .hero-sub { margin-left: auto; margin-right: auto; }
.hero-content .hero-cta { justify-content: center; }
.hero-content .hero-trust { justify-content: center; }
.hero-badge {
  display: inline-block;
  padding: 6px 14px;
  background: rgba(79, 140, 255, 0.12);
  border: 1px solid rgba(79, 140, 255, 0.3);
  color: #6f9eff;
  border-radius: 100px;
  font-size: 12.5px;
  font-weight: 600;
  margin-bottom: 20px;
}
.hero-title {
  /* font-size — Tailwind responsive utility text-[32px] sm:text-[42px] lg:text-[56px] (inbox-285) */
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin-bottom: 22px;
  color: #fff;
}
.hero-title .grad {
  background: linear-gradient(135deg, #4f8cff, #b366ff);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
}
.hero-sub {
  font-size: 18px; color: #9aa5b1; line-height: 1.55;
  max-width: 560px; margin-bottom: 32px;
}
.hero-cta { display: flex; gap: 12px; margin-bottom: 24px; flex-wrap: wrap; }
.hero-trust { display: flex; gap: 10px; flex-wrap: wrap; }
.trust-pill {
  padding: 5px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #1f2733;
  border-radius: 100px;
  font-size: 12px;
  color: #9aa5b1;
}

/* Hero demo dashboard (mockup landing-v1.html port) */
.demo-wrap {
  max-width: 980px;
  margin: 64px auto 0;
  position: relative;
}
.demo-wrap::before {
  content: ""; position: absolute; inset: -40px -60px;
  background: linear-gradient(135deg, rgba(79, 140, 255, 0.4), rgba(179, 102, 255, 0.3));
  opacity: 0.18; filter: blur(60px);
  border-radius: 50%; z-index: -1;
}
.demo {
  background: #1a1f29;
  border: 1px solid #2f3947;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.04) inset;
}
.demo-bar {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 14px;
  background: #0f1620;
  border-bottom: 1px solid #2a3340;
}
.demo-bar .dot {
  width: 11px; height: 11px; border-radius: 50%;
  background: #3a4554;
}
.demo-bar .url {
  flex: 1; margin: 0 16px;
  background: #0a0d12; border: 1px solid #2a3340;
  border-radius: 6px; padding: 5px 12px;
  font-family: "SF Mono", Menlo, monospace; font-size: 12.5px;
  color: #9aa5b1;
  display: flex; align-items: center; gap: 8px;
}
.demo-bar .url::before {
  content: ""; width: 10px; height: 10px; border-radius: 50%;
  border: 1.5px solid #4dd693;
}
.demo-body { display: grid; grid-template-columns: 220px 1fr; min-height: 380px; }
.demo-sidebar {
  background: #0f1620;
  padding: 18px 0;
  border-right: 1px solid #2a3340;
}
.demo-side-item {
  padding: 9px 18px; font-size: 13px; color: #9aa5b1;
  border-left: 2px solid transparent;
}
.demo-side-item.active {
  color: #fff;
  background: rgba(79, 140, 255, 0.12);
  border-left-color: #4f8cff;
}
.demo-main { padding: 22px; }
.demo-h { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 18px; }
.demo-h h3 { font-size: 17px; color: #fff; font-weight: 600; }
.demo-h .period { font-size: 12px; color: #6b7480; }
.demo-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 22px; }
.stat {
  background: #0f1620;
  border: 1px solid #2a3340;
  border-radius: 8px;
  padding: 12px 14px;
}
.stat .label { font-size: 11.5px; color: #6b7480; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 4px; }
.stat .value { font-size: 20px; font-weight: 700; color: #fff; letter-spacing: -0.01em; font-variant-numeric: tabular-nums; }
.stat .delta { font-size: 11.5px; margin-top: 2px; font-weight: 600; }
.stat .delta.up { color: #4dd693; }
.stat .delta.down { color: #ff6b6b; }
.demo-chart {
  height: 140px;
  background: #0f1620;
  border: 1px solid #2a3340;
  border-radius: 8px;
  padding: 14px 16px 8px;
  display: flex; align-items: flex-end; gap: 4px;
}
.demo-chart .bar {
  flex: 1;
  background: linear-gradient(180deg, #4f8cff 0%, rgba(79, 140, 255, 0.3) 100%);
  border-radius: 3px 3px 0 0;
  min-height: 8%;
}

/* Sections common */
.section-head { text-align: center; margin-bottom: 48px; }
.section-eyebrow {
  display: inline-block; padding: 4px 12px;
  background: rgba(79, 140, 255, 0.1); color: #6f9eff;
  border-radius: 100px; font-size: 11.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  margin-bottom: 14px;
}
.section-head h2 { font-size: 38px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; margin-bottom: 14px; color: #fff; }
.section-sub { font-size: 16px; color: #9aa5b1; line-height: 1.55; max-width: 640px; margin: 0 auto; }

/* Features */
.features { padding: 80px 0; background: #0a0d12; }
.feature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.feature {
  background: #1a1f29; border: 1px solid #2a3340;
  border-radius: 14px; padding: 24px 22px;
  transition: 0.15s;
}
.feature:hover { border-color: #4f8cff; transform: translateY(-2px); }
.feature-icon {
  width: 44px; height: 44px; border-radius: 11px;
  background: linear-gradient(135deg, rgba(79,140,255,0.18), rgba(179,102,255,0.18));
  color: #4f8cff;
  display: grid; place-items: center;
  margin-bottom: 14px;
}
.feature h3 { font-size: 16px; font-weight: 600; margin-bottom: 6px; color: #fff; }
.feature p { font-size: 13.5px; color: #9aa5b1; line-height: 1.55; }

/* AI */
.ai-section {
  padding: 80px 0;
  background: linear-gradient(180deg, #0a0d12, #0f1620);
}
.ai-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; }
.ai-content { min-width: 0; }
.ai-content h2 { font-size: 36px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; margin: 12px 0 18px; color: #fff; }
.ai-text { font-size: 15.5px; color: #9aa5b1; line-height: 1.6; margin-bottom: 20px; }
.ai-features { list-style: none; padding: 0; margin: 0 0 24px; display: flex; flex-direction: column; gap: 10px; }
.ai-features li { padding-left: 26px; position: relative; font-size: 14px; color: #c5cdd6; line-height: 1.55; }
.ai-features li::before { content: '✓'; position: absolute; left: 0; top: 0; width: 18px; height: 18px; border-radius: 50%; background: rgba(77,214,147,0.2); color: #4dd693; font-size: 11px; font-weight: 800; display: grid; place-items: center; }

.ai-chat {
  background: #1a1f29; border: 1px solid #2a3340; border-radius: 14px;
  padding: 20px; display: flex; flex-direction: column; gap: 14px;
}
.msg {
  padding: 12px 16px; border-radius: 12px; font-size: 13.5px;
  max-width: 92%; line-height: 1.5;
}
.msg.user { background: rgba(79,140,255,0.18); color: #fff; align-self: flex-end; border-bottom-right-radius: 4px; }
.msg.ai { background: #0f1620; color: #c5cdd6; align-self: flex-start; border-bottom-left-radius: 4px; }
.msg.ai strong { color: #fff; display: block; margin-bottom: 6px; }
.msg.ai ol { list-style: decimal inside; padding: 0; margin: 4px 0 8px; font-size: 12.5px; }
.msg.ai code { font-family: 'SF Mono', Menlo, monospace; font-size: 11.5px; color: #6f9eff; }
.msg-meta { display: block; font-size: 10.5px; color: #6b7480; margin-top: 4px; }

/* Integrations */
.integrations { padding: 72px 0; background: #0a0d12; }
.int-grid { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; max-width: 880px; margin: 0 auto; }
.int-chip {
  padding: 10px 18px; background: #1a1f29; border: 1px solid #2a3340;
  border-radius: 100px; font-size: 13.5px; color: #c5cdd6;
}

/* Pricing */
.pricing { padding: 80px 0; background: linear-gradient(180deg, #0f1620, #0a0d12); }
/* inbox-285 — mobile-first: 1 col <md, 2 col md→lg, 3 col ≥lg. 4-я карточка
   переходит на новый ряд при 3 cols — норма (acceptance явно 1/2/3). */
.pricing-grid { display: grid; grid-template-columns: 1fr; gap: 16px; max-width: 1080px; margin: 0 auto; }
@media (min-width: 768px) { .pricing-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .pricing-grid { grid-template-columns: repeat(3, 1fr); } }
.price-card {
  background: #1a1f29; border: 1px solid #2a3340;
  border-radius: 14px; padding: 24px 20px;
  display: flex; flex-direction: column; gap: 14px;
  position: relative;
}
.price-card.featured { border-color: #4f8cff; box-shadow: 0 0 0 1px #4f8cff, 0 24px 48px rgba(79,140,255,0.15); }
.featured-tag {
  position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
  padding: 4px 12px; background: linear-gradient(135deg, #4f8cff, #6f9eff);
  color: #fff; border-radius: 100px; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.tier-name { font-size: 12px; color: #6f9eff; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; }
.price { font-size: 32px; font-weight: 800; color: #fff; letter-spacing: -0.015em; }
.price small { font-size: 13px; font-weight: 500; color: #9aa5b1; margin-left: 4px; }
.price-desc { font-size: 12.5px; color: #9aa5b1; line-height: 1.45; }
.price-card ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; flex: 1; }
.price-card li { font-size: 13px; color: #c5cdd6; padding-left: 22px; position: relative; }
.price-card li::before { content: '✓'; position: absolute; left: 0; top: 0; color: #4dd693; font-weight: 700; }
.pricing-foot { text-align: center; font-size: 13.5px; color: #9aa5b1; margin-top: 32px; }
.pricing-foot a { color: #6f9eff; text-decoration: none; }
.pricing-foot a:hover { text-decoration: underline; }

/* Final CTA */
.final-cta {
  padding: 96px 0;
  background:
    radial-gradient(ellipse 50% 60% at 50% 50%, rgba(79, 140, 255, 0.18), transparent 60%),
    #0a0d12;
  text-align: center;
}
.final-cta h2 { font-size: 40px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; color: #fff; margin-bottom: 14px; }
.final-cta p { font-size: 17px; color: #9aa5b1; margin-bottom: 28px; }
.final-cta-actions { display: inline-flex; gap: 12px; flex-wrap: wrap; justify-content: center; }

/* Buttons */
.btn-primary, .btn-secondary, .btn-outline {
  display: inline-block;
  padding: 12px 22px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  transition: 0.15s;
}
.btn-primary {
  background: linear-gradient(135deg, #4f8cff, #6f9eff);
  color: #fff;
  box-shadow: 0 8px 20px rgba(79, 140, 255, 0.3);
}
.btn-primary.big { padding: 14px 28px; font-size: 16px; }
.btn-primary:hover { filter: brightness(1.1); }
.btn-secondary {
  background: rgba(255, 255, 255, 0.06);
  color: #e6eaf0;
  border: 1px solid #2a3340;
}
.btn-secondary:hover { background: rgba(255, 255, 255, 0.1); }
.btn-outline {
  background: transparent;
  color: #e6eaf0;
  border: 1px solid #2a3340;
  text-align: center;
}
.btn-outline:hover { border-color: #4f8cff; color: #fff; }

/* Responsive — inbox-285. pricing-grid правила вынесены в основной блок
   (mobile-first). Tailwind utility-классы покрывают hero-cta width и
   hero-title font-size — здесь оставлены не-CTA правки. */
@media (max-width: 980px) {
  .ai-grid { grid-template-columns: 1fr; gap: 40px; }
  .section-head h2 { font-size: 30px; }
  .feature-grid { grid-template-columns: repeat(2, 1fr); }
  .demo-body { grid-template-columns: 1fr; }
  .demo-sidebar { display: none; }
  .demo-stats { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .container { padding: 0 18px; }
  .hero { padding: 56px 0 64px; }
  .hero-sub { font-size: 15.5px; }
  .demo-wrap { margin-top: 40px; }
  .demo-wrap::before { display: none; }
  .demo-bar { padding: 9px 11px; gap: 6px; }
  .demo-bar .dot { width: 9px; height: 9px; }
  .demo-bar .url { font-size: 10.5px; padding: 4px 9px; margin: 0 10px; }
  .demo-main { padding: 16px; }
  .demo-stats { gap: 8px; margin-bottom: 16px; }
  .stat { padding: 10px 11px; }
  .stat .label { font-size: 10.5px; }
  .stat .value { font-size: 17px; }
  .features, .ai-section, .integrations, .pricing { padding: 56px 0; }
  .section-head h2 { font-size: 26px; }
  .ai-content h2 { font-size: 26px; }
  .feature-grid { grid-template-columns: 1fr; }
  .final-cta { padding: 64px 0; }
  .final-cta h2 { font-size: 28px; }
  .final-cta p { font-size: 15px; }
}
</style>
